import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { CreateProductDto, ProductDto } from "../../../models/product/dto/product.dto";
import { GET_PRODUCT_QUERY_KEY } from "./useGetProduct";
import { GET_PRODUCTS_QUERY_KEY } from "./useListProducts";

export type CreateProductWithImageInput = {
    productData: Omit<CreateProductDto, 'imageUrl'>;
    imageFile?: File;
};

export const useCreateProduct = () => {
    const client = useApiClient();
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['createProduct'],
        mutationFn: (input: CreateProductDto | CreateProductWithImageInput) => {
            // Check if it's the new format with image file
            if ('productData' in input) {
                const formData = new FormData();

                // Append all product data fields
                Object.entries(input.productData).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        if (Array.isArray(value)) {
                            formData.append(key, JSON.stringify(value));
                        } else {
                            formData.append(key, value.toString());
                        }
                    }
                });

                // Append image file if provided
                if (input.imageFile) {
                    formData.append('image', input.imageFile);
                }

                return client.fetchApiWithFormData<ProductDto>({
                    endpoint: 'products/with-image',
                    method: 'POST',
                    formData
                });
            } else {
                // Legacy format - regular JSON request
                return client.fetchApi<ProductDto, CreateProductDto>({
                    endpoint: 'products',
                    method: 'POST',
                    body: input
                });
            }
        },
        onSuccess: (data, variables) => {
            queryClient.setQueryData(GET_PRODUCT_QUERY_KEY(data.id), () => data);

            const locationId = 'productData' in variables ? variables.productData.locationId : variables.locationId;
            queryClient.setQueryData(GET_PRODUCTS_QUERY_KEY(locationId), (prevList: ProductDto[] = []) => {
                return [...prevList, data].sort((a, b) => {
                    return a.name.localeCompare(b.name);
                });
            });
        }
    });
};
